import { ETAPAS_PEDIDO, ROTULO_ETAPA, type EtapaPedido, type PedidoCardDTO } from '@viniko/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { AvatarContato, useToast } from '../../componentes/ui';
import { api } from '../../lib/api';
import { useCorAtendente } from '../../lib/tema';
import { duracaoCurta, minutosDesde } from '../../lib/tempo';
import { useAgora } from '../../lib/useAgora';
import './pedidos.css';

const DIA = 24 * 60;

function Cartao({ p, agora }: { p: PedidoCardDTO; agora: number }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const cor = useCorAtendente(p.atendente?.cor);
  const parado = minutosDesde(p.naEtapaDesde, agora) ?? 0;
  const atrasado = p.etapa !== 'entregue' && parado >= 2 * DIA;

  async function mover(etapa: EtapaPedido) {
    if (!p.conversaId) {
      toast('Esse cliente não tem conversa pra registrar a mudança.', 'erro');
      return;
    }
    try {
      await api(`/api/pedidos/${p.id}/etapa`, {
        metodo: 'PATCH',
        corpo: { etapa, conversaId: p.conversaId },
      });
      qc.invalidateQueries({ queryKey: ['pedidos'] });
      toast(`Pedido movido pra ${ROTULO_ETAPA[etapa]}`);
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }

  return (
    <article
      className="ped-cartao"
      style={{ '--ped-cor': p.atendente ? cor.texto : 'var(--line)' } as CSSProperties}
    >
      <button
        className="ped-abrir"
        disabled={!p.conversaId}
        title={p.conversaId ? 'Abrir a conversa' : undefined}
        onClick={() => p.conversaId && navegar(`/conversas/${p.conversaId}`)}
      >
        <span className="ped-quem">
          <AvatarContato id={p.contato.id} nome={p.contato.nome} tamanho={28} />
          <b>{p.contato.nome}</b>
        </span>
        {p.itens.length > 0 ? (
          <span className="ped-itens">
            {p.itens.map((i, n) => (
              <span key={n}>
                {i.material}
                {i.quantidade ? ` · ${i.quantidade.toLocaleString('pt-BR')} un` : ''}
              </span>
            ))}
          </span>
        ) : (
          <span className="ped-itens ped-sem-itens">Sem itens do orçamento</span>
        )}
        <span className="ped-rodape">
          <span className={atrasado ? 'ped-tempo atrasado' : 'ped-tempo'}>
            há {duracaoCurta(parado)} na etapa
          </span>
          {p.tinyNumero && <span className="ped-num">Tiny {p.tinyNumero}</span>}
          {p.osNumero && <span className="ped-num">OS {p.osNumero}</span>}
        </span>
        {p.atendente && <span className="ped-atendente">{p.atendente.nome}</span>}
      </button>
      <select
        className="ped-mover"
        aria-label={`Mover pedido de ${p.contato.nome}`}
        value=""
        onChange={(e) => void mover(e.target.value as EtapaPedido)}
      >
        <option value="" disabled>
          Mover pra…
        </option>
        {ETAPAS_PEDIDO.filter((e) => e !== p.etapa).map((e) => (
          <option key={e} value={e}>
            {ROTULO_ETAPA[e]}
          </option>
        ))}
      </select>
    </article>
  );
}

export function Pedidos() {
  const agora = useAgora();
  const [busca, setBusca] = useState('');
  const pedidos = useQuery({
    queryKey: ['pedidos'],
    queryFn: () => api<PedidoCardDTO[]>('/api/pedidos'),
    refetchInterval: 30_000,
  });

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    const lista = pedidos.data ?? [];
    if (!t) return lista;
    return lista.filter(
      (p) =>
        p.contato.nome.toLowerCase().includes(t) ||
        p.itens.some((i) => i.material.toLowerCase().includes(t)) ||
        p.tinyNumero?.includes(t) ||
        p.osNumero?.includes(t),
    );
  }, [pedidos.data, busca]);

  return (
    <div className="pedidos">
      <header className="ped-topo">
        <h1>Pedidos</h1>
        <label className="ped-busca">
          <Search aria-hidden />
          <input
            className="input"
            placeholder="Buscar cliente, material ou número"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </label>
        <span className="ped-total">{filtrados.length} pedidos</span>
      </header>

      {pedidos.isLoading ? (
        <p className="nota-sec ped-msg">Carregando…</p>
      ) : pedidos.error ? (
        <p className="erro-form ped-msg">{(pedidos.error as Error).message}</p>
      ) : (
        <div className="ped-quadro">
          {ETAPAS_PEDIDO.map((etapa) => {
            const daEtapa = filtrados.filter((p) => p.etapa === etapa);
            return (
              <section key={etapa} className={`ped-coluna etapa-${etapa}`}>
                <h2>
                  {ROTULO_ETAPA[etapa]} <small>{daEtapa.length}</small>
                </h2>
                <div className="ped-cartoes">
                  {daEtapa.map((p) => (
                    <Cartao key={p.id} p={p} agora={agora} />
                  ))}
                  {daEtapa.length === 0 && <p className="ped-vazio">Nenhum pedido</p>}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
